/**
 * @format
 */
// Migrated from AngularJS: degreeProgrammeAttainmentApplication.service.js
import { Injectable } from '@angular/core';
import { ModulePlanState, PlanValidationResult, PlanValidationTs, ValidatablePlan } from 'common-typescript';
import {
    DegreeProgrammeAttainmentApplication,
    Module,
    ModuleAttainmentApplication,
    OtmId,
    StudentWorkflow,
    StudyRight,
} from 'common-typescript/types';
import { forkJoin, Observable, throwError } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AuthService } from 'sis-common';
import { STUDENT_APPLICATION_STATE, StudentApplicationEntityService, WorkflowEntityService } from 'sis-components';
import { isModulePlannedForAttainment, ModuleAttainmentApplicationService } from './module-attainment-application.service';

/**
 * Thrown by {@link DegreeProgrammeAttainmentApplicationService.create} when an active (REQUESTED / IN_HANDLING)
 * degree programme attainment application already exists for the module. Carries the existing application id
 * (equal to the workflow id) so the caller can route the student to it instead of showing a raw error.
 */
export class DegreeProgrammeAttainmentApplicationExistsError extends Error {
    readonly moduleId: OtmId;
    readonly existingApplicationId: OtmId;

    constructor(moduleId: OtmId, existingApplicationId: OtmId) {
        super(`Application already exists for degree programme ${moduleId}.`);
        this.name = 'DegreeProgrammeAttainmentApplicationExistsError';
        this.moduleId = moduleId;
        this.existingApplicationId = existingApplicationId;
    }
}

@Injectable({
    providedIn: 'root',
})
export class DegreeProgrammeAttainmentApplicationService {
    constructor(
        private authService: AuthService,
        private studentApplicationEntityService: StudentApplicationEntityService,
        private workflowEntityService: WorkflowEntityService,
        private moduleAttainmentApplicationService: ModuleAttainmentApplicationService,
    ) {}

    findByModuleAndStates(moduleId: OtmId, states: string[]): Observable<DegreeProgrammeAttainmentApplication[]> {
        return this.studentApplicationEntityService
            .getApplicationsForStudentByTypes(this.authService.personId(), ['DEGREE_PROGRAMME_ATTAINMENT_APPLICATION'])
            .pipe(
                map(applications =>
                    (applications as DegreeProgrammeAttainmentApplication[]).filter(
                        application => application.moduleId === moduleId && states.includes(application.state),
                    ),
                ),
            );
    }

    existsByModuleAndStates(moduleId: OtmId, states: string[]): Observable<boolean> {
        return this.findByModuleAndStates(moduleId, states).pipe(map(applications => applications.length > 0));
    }

    /**
     * Returns the student's active (REQUESTED / IN_HANDLING) degree programme attainment application for the given
     * module, or undefined if none exists. Used both to block entry to the graduation wizard and as the submit-time
     * guard, so the "an application already exists" definition lives in one place.
     */
    findActiveApplicationForModule(moduleId: OtmId): Observable<DegreeProgrammeAttainmentApplication | undefined> {
        return this.findByModuleAndStates(moduleId, [STUDENT_APPLICATION_STATE.REQUESTED, STUDENT_APPLICATION_STATE.IN_HANDLING]).pipe(
            map(applications => applications[0]),
        );
    }

    isPlanStateValidForApplicationSending(planState: ModulePlanState | null): boolean {
        return planState?.contentApproval === 'REQUIRED' || isModulePlannedForAttainment(planState);
    }

    create(
        application: Partial<DegreeProgrammeAttainmentApplication>,
        module: Module,
        studyRight: StudyRight,
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
    ): Observable<(ModuleAttainmentApplication | StudentWorkflow | undefined)[]> {
        if (module.type !== 'DegreeProgramme') {
            return throwError(() => `Module ${module.id} is not a degree programme.`);
        }

        if (
            !this.isPlanStateValidForApplicationSending(
                PlanValidationTs.getModulePlanState(validatablePlan.modulesById[module.id], validatablePlan, planValidationResult),
            )
        ) {
            return throwError(() => `Module ${module.id} is not in correct plan state.`);
        }

        if (!validatablePlan.plan?.primary) {
            return throwError(() => `Plan ${validatablePlan.plan?.id} is not primary.`);
        }

        return this.findActiveApplicationForModule(module.id).pipe(
            take(1),
            switchMap(existingApplication => {
                if (existingApplication) {
                    return throwError(() => new DegreeProgrammeAttainmentApplicationExistsError(module.id, existingApplication.id));
                }

                const childModules = validatablePlan
                    .getSelectedModulesUnderModule(module)
                    .filter(
                        (selectedModule: Module) =>
                            selectedModule.type === 'StudyModule' ||
                            selectedModule.type === 'GroupingModule' ||
                            (selectedModule as any).graded === false,
                    );

                const observables: Observable<ModuleAttainmentApplication | StudentWorkflow | undefined>[] = childModules.map(
                    (childModule: Module) =>
                        this.moduleAttainmentApplicationService.sendApplication(
                            childModule,
                            validatablePlan,
                            studyRight,
                            planValidationResult,
                            true,
                        ),
                );

                const workflowApplication = this.createWorkflowApplication(application, module, studyRight, validatablePlan);
                observables.push(this.workflowEntityService.createAsStudent(workflowApplication));

                return forkJoin(observables);
            }),
        );
    }

    private createWorkflowApplication(
        application: Partial<DegreeProgrammeAttainmentApplication>,
        module: Module,
        studyRight: StudyRight,
        validatablePlan: ValidatablePlan,
    ): Partial<DegreeProgrammeAttainmentApplication> {
        const { plan } = validatablePlan;
        return {
            ...application,
            moduleId: module.id,
            planId: plan.id,
            studyRightId: studyRight.id,
            state: 'REQUESTED',
            type: 'DegreeProgrammeAttainmentApplication',
            planContent: {
                courseUnitSelections: structuredClone(plan.courseUnitSelections),
                moduleSelections: structuredClone(plan.moduleSelections),
                customCourseUnitAttainmentSelections: structuredClone(plan.customCourseUnitAttainmentSelections),
                customModuleAttainmentSelections: structuredClone(plan.customModuleAttainmentSelections),
                customStudyDrafts: structuredClone(plan.customStudyDrafts),
            },
        };
    }
}
